import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CatalogueItem, CommunityGroupInput, FREQUENCIES, CYCLING_DISCIPLINES, CYCLING_TERRAINS, EXPERIENCE_LEVELS } from 'backend/interfaces/community-groups';
import { CommunityConfiguration, CommunityGroupService } from './community-group.service';
import { CommunityMutationSession } from './mutation-session';

@Component({
  selector: 'app-community-group-form',
  templateUrl: './community-group-form.component.html',
  styleUrls: ['./community-groups.scss']
})
export class CommunityGroupFormComponent implements OnInit {
  @Input() embedded = false;
  @Input() portal: 'admin' | 'user';
  @Output() saved = new EventEmitter<void>();
  id: string; loading = true; busy = false; error = '';
  adminPortal = false;
  backLink = '/user/groups';
  config: CommunityConfiguration;
  type: 'golf' | 'cycling' = 'golf';
  name = ''; description = ''; rules = ''; requirements = ''; frequency: any = 'weekly'; customFrequency = '';
  logo: { name: string; url: string };
  areas: CatalogueItem[] = []; courses: CatalogueItem[] = [];
  handicapMin: number; handicapMax: number;
  levels: any[] = []; disciplines: any[] = []; terrains: any[] = [];
  frequencies = FREQUENCIES; levelOptions = EXPERIENCE_LEVELS; disciplineOptions = CYCLING_DISCIPLINES; terrainOptions = CYCLING_TERRAINS;
  private mutations: CommunityMutationSession;
  constructor(private service: CommunityGroupService, private route: ActivatedRoute, private router: Router) { this.mutations = new CommunityMutationSession(service); }
  async ngOnInit(): Promise<void> {
    this.adminPortal = this.portal === 'admin' || this.route.snapshot.data?.portal === 'admin';
    this.backLink = this.adminPortal ? '/admin/community-groups' : '/user/groups';
    this.id = this.embedded ? null : this.route.snapshot.paramMap.get('id');
    try {
      this.config = await this.service.configuration();
      if (this.id) {
        const group = await this.service.get(this.id);
        if (!group.viewer.isAdmin) throw new Error('Only the group administrator can edit this group.');
        this.type = group.groupType; this.name = group.name; this.description = group.description;
        this.logo = { name: group.logoFileName, url: group.logoUrl }; this.areas = group.areas;
        this.frequency = group.frequencyCode; this.customFrequency = group.frequencyCustom || '';
        this.rules = group.rules || ''; this.requirements = group.membershipRequirements || '';
        if (group.golf) { this.courses = group.golf.courses; this.handicapMin = group.golf.handicapMin; this.handicapMax = group.golf.handicapMax; }
        if (group.cycling) { this.levels = group.cycling.experienceLevels; this.disciplines = group.cycling.disciplines; this.terrains = group.cycling.terrains; }
      }
    } catch (error) { this.error = error.message || 'Could not load the group editor.'; this.config = undefined; }
    finally { this.loading = false; }
  }
  async selectLogo(event: Event): Promise<void> {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (!file) return;
    this.busy = true; this.error = '';
    try { this.logo = await this.service.uploadLogo(file); }
    catch (error) { this.error = error.message || 'Logo upload failed.'; }
    finally { this.busy = false; }
  }
  get configurationIssues(): string[] {
    if (this.loading) return [];
    if (!this.config) return ['Could not load group setup. Save will retry without clearing your entries.'];
    const issues: string[] = [];
    if (!this.config.enabled) issues.push('Community groups are disabled on the server.');
    else if (!this.config.writesEnabled) issues.push('Group saving is disabled on the server.');
    if (!this.config.categoryIds?.[this.type]) issues.push(`${this.type === 'golf' ? 'Golf' : 'Cycling'} has no category assigned on the server.`);
    if (issues.length) issues.push('A platform administrator must finish the backend setup. Your entries will remain here while you retry.');
    return issues;
  }

  async save(): Promise<void> {
    if (this.busy || this.loading) return;
    this.busy = true; this.error = '';
    try {
      // Refresh missing setup so an administrator can configure the server and retry this same form.
      if (!this.config?.categoryIds?.[this.type]) this.config = await this.service.configuration();
      const categoryId = this.config?.categoryIds?.[this.type];
      if (!categoryId) throw new Error(`${this.type === 'golf' ? 'Golf' : 'Cycling'} has no category assigned on the server. Complete the backend setup, then save again. Your entries have been kept.`);
      const common = { name: this.name, description: this.description, categoryId,
        logoUrl: this.logo?.url, logoFileName: this.logo?.name, areaIds: this.areas.map(a => a.id), frequencyCode: this.frequency,
        frequencyCustom: this.customFrequency, rules: this.rules.trim() || undefined, membershipRequirements: this.requirements.trim() || undefined };
      const group: CommunityGroupInput = this.type === 'golf' ? { ...common, groupType: 'golf', golf: { courseIds: this.courses.map(c => c.id), handicapMin: this.handicapMin ?? undefined, handicapMax: this.handicapMax ?? undefined } } :
        { ...common, groupType: 'cycling', cycling: { experienceLevels: this.levels, disciplines: this.disciplines, terrains: this.terrains } };
      const method = this.id ? 'update' : this.adminPortal ? 'adminCreate' : 'create';
      const result = await this.mutations.run(method, this.id ? { groupId: this.id, group } : { group });
      if (this.embedded) { this.saved.emit(); return; }
      await this.router.navigate(this.adminPortal ? ['/admin/community-groups'] : ['/groups', result.id]);
    } catch (error) { this.error = error.message || 'Could not save the group. You can retry.'; }
    finally { this.busy = false; }
  }
}
